/**
 * Format a number with locale-specific formatting
 * @param n - Number to format
 * @param d - Decimal places (default: 2)
 * @returns Formatted number or '-' if invalid
 */
export function fmt(n: number | null | undefined, d: number = 2): string {
  if (n == null || isNaN(n)) return '-';
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: d,
    maximumFractionDigits: d
  });
}

// Add methods to fmt function for new API
Object.assign(fmt, {
  /**
   * Format number with locale formatting (no decimals by default for whole numbers)
   */
  number: (n: number | null | undefined, decimals: number = 0): string => {
    if (n == null || isNaN(n)) return '-';
    return Number(n).toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  },

  /**
   * Format as currency (USD)
   */
  currency: (n: number | null | undefined): string => {
    if (n == null || isNaN(n)) return '$-';
    return '$' + Number(n).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  },

  /**
   * Format percentage
   */
  percent: (n: number | null | undefined, decimals: number = 1): string => {
    if (n == null || isNaN(n)) return '-';
    return Number(n).toFixed(decimals) + '%';
  }
});

/**
 * Convert date to ISO format (YYYY-MM-DD)
 * @param d - Date to format
 * @returns ISO formatted date
 */
export function iso(d: Date | string | number): string {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

/**
 * Generate unique ID
 * @returns Unique identifier
 */
export function id(): string {
  return 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

/**
 * Sleep/delay utility
 * @param ms - Milliseconds to sleep
 * @returns Promise that resolves after delay
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

/**
 * Debounce function calls
 * @param func - Function to debounce
 * @param wait - Wait time in ms (default: 500)
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number = 500
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function executedFunction(...args: Parameters<T>): void {
    const later = () => {
      timeout = null;
      func(...args);
    };
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
